import { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import { getStoredToken } from '../../utils/authStorage';

export default function CartsTable() {
    const [carts, setCarts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchCarts = async () => {
        setLoading(true);
        setError(null);
        try {
            // Using manual fetch because SDK might not have the new /api/admin/carts yet
            const token = getStoredToken();
            const baseUrl = import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000';
            
            const response = await fetch(`${baseUrl}/api/admin/carts?only_authenticated=1`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                if (response.status === 401) throw new Error('Unauthenticated');
                throw new Error('Failed to fetch carts');
            }

            const res = await response.json();
            
            // Extract data from pagination structure
            let data = [];
            if (res && typeof res === 'object') {
                if (Array.isArray(res)) {
                    data = res;
                } else if (Array.isArray(res.data)) {
                    data = res.data;
                } else if (res.data && Array.isArray(res.data.data)) {
                    data = res.data.data;
                }
            }
            
            setCarts(data);
        } catch (err: any) {
            console.error('Failed to fetch carts:', err);
            setError(err?.message || 'Failed to fetch carts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCarts();
    }, []);

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Abandoned Carts</h3>
                <button
                    onClick={fetchCarts}
                    disabled={loading}
                    className="text-sm text-brand-500 hover:text-brand-600 disabled:opacity-50"
                >
                    {loading ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            {error && <div className="p-4 text-red-500">{error}</div>}

            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start">Cart ID</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Date</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Customer</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Items Count</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Total Price</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {carts.length === 0 && !loading ? (
                            <TableRow><TableCell colSpan={6} className="px-5 py-4 text-center">No abandoned carts found.</TableCell></TableRow>
                        ) : (
                            carts.map((cart) => (
                                <TableRow key={cart.id}>
                                    <TableCell className="px-5 py-4">#{cart.id}</TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500">
                                        {new Date(cart.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-900 dark:text-white">
                                                {cart.user ? `${cart.user.first_name || ''} ${cart.user.last_name || ''}`.trim() || cart.user.username : 'Guest'}
                                            </span>
                                            <span className="text-xs text-gray-500">{cart.user?.email}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        {cart.total_items} items
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-900 dark:text-white font-medium">
                                        ${parseFloat(cart.total_price).toFixed(2)}
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        <Badge
                                            size="sm"
                                            color={cart.total_items > 0 ? "success" : "warning"}
                                        >
                                            {cart.total_items > 0 ? "Active" : "Empty"}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
